<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const isUser = computed(() => props.message.role === 'user');
const isError = computed(() => props.message.role === 'error');

const configPreview = computed(() => {
  if (!props.message.config) return null;
  const c = props.message.config;
  return {
    eventName: c.event_name,
    reportName: c.report_name,
    metricsCount: c.metrics?.length || 0,
    dimensionsCount: c.dimensions?.length || 0,
    visualization: c.default_visualization,
  };
});
</script>

<template>
  <div
    class="flex w-full mb-3"
    :class="isUser ? 'justify-end' : 'justify-start'"
  >
    <div
      class="max-w-[85%] rounded-xl px-4 py-3 text-sm"
      :class="{
        'bg-n-brand text-white': isUser,
        'bg-n-surface-2 text-n-slate-12': !isUser && !isError,
        'bg-r-50 text-r-800 dark:bg-r-900/20 dark:text-r-400': isError,
      }"
    >
      <p v-if="isUser" class="whitespace-pre-wrap mb-0">
        {{ message.content }}
      </p>

      <p v-else-if="isError" class="mb-0">
        {{ message.content }}
      </p>

      <template v-else>
        <p v-if="message.content" class="whitespace-pre-wrap mb-0">
          {{ message.content }}
        </p>

        <div
          v-if="configPreview"
          class="mt-3 rounded-lg border border-n-weak bg-n-surface-1 p-3"
        >
          <div class="flex items-center gap-2 mb-2">
            <span class="i-lucide-file-bar-chart text-n-brand w-4 h-4" />
            <span class="font-medium text-n-slate-12">
              {{ configPreview.reportName }}
            </span>
          </div>
          <div class="flex flex-wrap gap-3 text-xs text-n-slate-11">
            <span class="flex items-center gap-1">
              <span class="i-lucide-tag w-3 h-3" />
              {{ configPreview.eventName }}
            </span>
            <span class="flex items-center gap-1">
              <span class="i-lucide-bar-chart-3 w-3 h-3" />
              {{ configPreview.metricsCount }}
              {{ t('WGPT_CUSTOM_REPORTS.CHAT.METRICS') }}
            </span>
            <span class="flex items-center gap-1">
              <span class="i-lucide-layers w-3 h-3" />
              {{ configPreview.dimensionsCount }}
              {{ t('WGPT_CUSTOM_REPORTS.CHAT.DIMENSIONS') }}
            </span>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
